import { useEffect, useState } from "react";
import { supabase } from "../../lib/supabaseClient";
import type { EnrichedParticipant } from "../../types/participants";
import FloatingActionButton from "../../components/FloatingActionButton";
import PaymentForm from "./PaymentForm";
import NewExpenseForm from "./NewExpenseForm";
import ReisekassenOverview from "./ReisekassenOverview"; // oder "../components/expenses/ReisekassenOverview"

interface Expense {
  id: string;
  title: string;
  category: string;
  amount: number;
  payer_id: string | null;
  trip_id: string | null;
  created_at: string | null;
}

interface Payment {
  id: string;
  amount: number;
  trip_id: string | null;
  user_id: string | null;
  created_at: string | null;
  profiles: {
    id: string;
    email: string | null;
    first_name: string | null;
    last_name: string | null;
  } | null;
}

interface ExpenseListProps {
  tripId: string;
  refreshTrigger?: number;
  onChange?: () => void;
  isOrganizer: boolean;
}

interface Share {
  expense_id: string;
  user_id: string;
  amount: number;
  is_paid: boolean | null;
}

interface ExpenseListProps {
  tripId: string;
  refreshTrigger?: number;
  onChange?: () => void;
  isOrganizer: boolean;
  openExpenseModal?: boolean;
  openPaymentModal?: boolean;
  participants?: EnrichedParticipant[];
  onCloseModals?: () => void;
  onNewExpense?: () => void;
  hideInternalFab?: boolean;
}

export default function ExpenseList({
  tripId,
  refreshTrigger,
  onChange,
  isOrganizer,
  openExpenseModal,
  openPaymentModal,
  participants: externalParticipants,
  onCloseModals,
  onNewExpense,
  hideInternalFab,
}: ExpenseListProps) {
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [participants, setParticipants] = useState<EnrichedParticipant[]>([]);
  const [loading, setLoading] = useState(true);
  const [shares, setShares] = useState<Share[]>([]);

  const [selectedExpense, setSelectedExpense] = useState<Expense | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showNewExpenseModal, setShowNewExpenseModal] = useState(false);
  const [showNewPaymentModal, setShowNewPaymentModal] = useState(false);

  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState("");

  const fetchExpenses = async () => {
    setLoading(true);

    const { data: expenseData } = await supabase
      .from("trip_expenses")
      .select("*")
      .eq("trip_id", tripId)
      .order("created_at", { ascending: false });

    const { data: paymentData } = await supabase
      .from("trip_payments")
      .select(`*, profiles!user_id(id, email, first_name, last_name)`)
      .eq("trip_id", tripId)
      .order("created_at", { ascending: false });

    const { data: participantData } = await supabase
      .from("trip_participants")
      .select("user_id, is_blocked")
      .eq("trip_id", tripId);

    const userIds = participantData?.map((p) => p.user_id).filter((id): id is string => id !== null) || [];

    const { data: profilesData } = await supabase
      .from("profiles")
      .select("id, email, first_name, last_name")
      .in("id", userIds);

    const enriched: EnrichedParticipant[] = (participantData ?? [])
      .filter((p) => p.user_id !== null)
      .map((p) => {
        const prof = profilesData?.find((u) => u.id === p.user_id);
        return {
          trip_id: tripId,
          user_id: p.user_id as string,
          email: prof?.email ?? null,
          first_name: prof?.first_name ?? "",
          last_name: prof?.last_name ?? "",
          profile_picture_url: null, // oder: prof?.profile_picture_url ?? null
          is_blocked: p.is_blocked ?? false,
          is_co_organizer: false, // 👈 wichtig für Typ
        };
      });

    const { data: shareData } = await supabase
      .from("trip_expense_shares")
      .select("*")
      .in("expense_id", expenseData?.map((e) => e.id) || []);
    setShares(shareData || []);

    setParticipants(enriched);

    setExpenses(expenseData || []);
    setPayments((paymentData || []) as Payment[]);

    setLoading(false);
  };

  useEffect(() => {
    fetchExpenses();
  }, [tripId, refreshTrigger]);

  useEffect(() => {
    if (openExpenseModal) {
      setShowNewExpenseModal(true);
    }
    if (openPaymentModal) {
      setShowNewPaymentModal(true);
    }
  }, [openExpenseModal, openPaymentModal]);

  const handleEdit = async () => {
    if (!selectedExpense || selectedUsers.length === 0) return;
    setSaving(true);

    const { error } = await supabase
      .from("trip_expenses")
      .update({
        title: selectedExpense.title,
        amount: selectedExpense.amount,
        category: selectedExpense.category,
        payer_id: selectedExpense.payer_id,
      })
      .eq("id", selectedExpense.id);

    if (error) {
      console.error("Fehler beim Aktualisieren:", error.message);
      setSaving(false);
      return;
    }

    const isPayerInvolved =
      selectedExpense.payer_id !== null &&
      selectedUsers.includes(selectedExpense.payer_id);

    const sharePerUser = parseFloat(
      (selectedExpense.amount / selectedUsers.length).toFixed(2)
    );

    await supabase
      .from("trip_expense_shares")
      .delete()
      .eq("expense_id", selectedExpense.id);

    const newShares = selectedUsers.map((userId) => ({
      expense_id: selectedExpense.id,
      user_id: userId,
      amount: sharePerUser,
      is_paid: false,
    }));

    await supabase.from("trip_expense_shares").insert(newShares);

    setShowEditModal(false);
    setSelectedExpense(null);
    setSaving(false);
    fetchExpenses();
    onChange?.();
  };

  const handleDelete = async () => {
    if (!selectedExpense) return;
    await supabase.from("trip_expenses").delete().eq("id", selectedExpense.id);
    await supabase
      .from("trip_expense_shares")
      .delete()
      .eq("expense_id", selectedExpense.id);
    setShowEditModal(false);
    setSelectedExpense(null);
    fetchExpenses();
    onChange?.();
  };

  const handleUpdatePayment = async () => {
    if (!selectedPayment || isNaN(parseFloat(paymentAmount))) return;

    await supabase
      .from("trip_payments")
      .update({ amount: parseFloat(paymentAmount) })
      .eq("id", selectedPayment.id);

    setSelectedPayment(null);
    setShowPaymentModal(false);
    fetchExpenses();
    onChange?.();
  };

  const handleDeletePayment = async () => {
    if (!selectedPayment) return;

    await supabase.from("trip_payments").delete().eq("id", selectedPayment.id);

    setSelectedPayment(null);
    setShowPaymentModal(false);
    fetchExpenses();
    onChange?.();
  };

  const toggleUser = (id: string) => {
    setSelectedUsers((prev) =>
      prev.includes(id) ? prev.filter((u) => u !== id) : [...prev, id]
    );
  };

  const totalsByCategory: Record<string, number> = {};
  let totalAmount = 0;
  expenses.forEach((exp) => {
    const cat = exp.category || "Sonstiges";
    totalsByCategory[cat] = (totalsByCategory[cat] || 0) + exp.amount;
    totalAmount += exp.amount;
  });

  return (
    <div className="mt-6">
      <h3 className="text-xs uppercase tracking-widest text-slate-500 font-medium mb-5 px-1">
        Buchhaltung
      </h3>
      <ReisekassenOverview
        saldoReisekasse={
          payments.reduce((sum, p) => sum + p.amount, 0) -
          expenses
            .filter((e) => !e.payer_id)
            .reduce((sum, e) => sum + e.amount, 0)
        }
        totalEinzahlungen={payments.reduce((sum, p) => sum + p.amount, 0)}
        totalAusgaben={expenses
          .filter((e) => !e.payer_id)
          .reduce((sum, e) => sum + e.amount, 0)}
        userTotals={(() => {
          const userTotals: Record<string, { owed: number; paid: number }> = {};

          // 1. Schulden aus shares berechnen
          shares.forEach((s) => {
            if (!userTotals[s.user_id]) {
              userTotals[s.user_id] = { owed: 0, paid: 0 };
            }
            userTotals[s.user_id].owed += s.amount;
          });

          // 2. Zahlungen (individuell)
          payments.forEach((p) => {
            if (!p.user_id) return;
            if (!userTotals[p.user_id]) {
              userTotals[p.user_id] = { owed: 0, paid: 0 };
            }
            userTotals[p.user_id].paid += p.amount;
          });

          // 3. Allgemeine Zahlung auf alle NICHT blockierten Teilnehmer verteilen
          const unblockedParticipants = participants.filter(
            (p) => !p.is_blocked
          );
          const allgemeine = payments
            .filter((p) => !p.user_id)
            .reduce((sum, p) => sum + p.amount, 0);
          const share =
            unblockedParticipants.length > 0
              ? allgemeine / unblockedParticipants.length
              : 0;

          unblockedParticipants.forEach((p) => {
            if (!userTotals[p.user_id]) {
              userTotals[p.user_id] = { owed: 0, paid: 0 };
            }
            userTotals[p.user_id].paid += share;
          });

          // 4. Gutschrift an Zahler für Ausgaben
          expenses.forEach((e) => {
            if (!e.payer_id) return;
            if (!userTotals[e.payer_id]) {
              userTotals[e.payer_id] = { owed: 0, paid: 0 };
            }
            userTotals[e.payer_id].paid += e.amount;
          });

          return userTotals;
        })()}
        users={participants.map((p) => ({
          id: p.user_id,
          email: p.email || "",
          first_name: p.first_name ?? undefined,
          last_name: p.last_name ?? undefined,
        }))}
      />

      {expenses.length > 0 && (
        <>
          {/* Ausgaben divider */}
          <div className="flex items-center gap-3 mb-3">
            <span className="text-[10px] uppercase tracking-widest text-slate-600">💰 Ausgaben</span>
            <div className="flex-1 h-px bg-[#2a3347]" />
          </div>

          {/* Kategorie-Zusammenfassung */}
          <div className="bg-[#161b27] rounded-xl border border-[#2a3347] p-4 mb-3">
            {Object.entries(totalsByCategory).map(([category, amount]) => (
              <div key={category} className="flex justify-between text-xs py-0.5">
                <span className="text-slate-400">{category}</span>
                <span className="text-slate-300 tabular-nums">{amount.toFixed(2)}</span>
              </div>
            ))}
            <div className="flex justify-between text-xs font-semibold pt-2 mt-1.5 border-t border-[#1e2639]">
              <span className="text-slate-400">Gesamt</span>
              <span className="text-slate-100 tabular-nums">{totalAmount.toFixed(2)}</span>
            </div>
          </div>

          {/* Ausgaben-Liste */}
          <div className="space-y-2 mb-6">
            {expenses.map((exp) => {
              const payer = participants.find((p) => p.user_id === exp.payer_id);
              const payerName = payer
                ? ([payer.first_name, payer.last_name].filter(Boolean).join(" ").trim() || payer.email || "Unbekannt")
                : "Reisekasse";

              return (
                <div
                  key={exp.id}
                  onClick={() => {
                    if (!isOrganizer) return;
                    setSelectedExpense(exp);
                    setShowEditModal(true);
                    supabase
                      .from("trip_expense_shares")
                      .select("user_id")
                      .eq("expense_id", exp.id)
                      .then(({ data }) =>
                        setSelectedUsers(data?.map((d) => d.user_id) || [])
                      );
                  }}
                  className={`bg-[#161b27] rounded-xl border border-[#2a3347] px-4 py-3 flex justify-between items-center ${
                    isOrganizer ? "cursor-pointer hover:border-sky-500/20 transition-colors" : ""
                  }`}
                >
                  <div>
                    <div className="text-sm font-medium text-slate-100">{exp.title}</div>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#1e2639] text-slate-500">{exp.category}</span>
                      <span className="text-[10px] text-slate-600">{payerName}</span>
                    </div>
                  </div>
                  <div className="text-sm font-semibold text-slate-100 tabular-nums shrink-0 ml-3">
                    {exp.amount.toFixed(2)}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {payments.length > 0 && (
        <div className="mt-2">
          {/* Einzahlungen divider */}
          <div className="flex items-center gap-3 mb-3">
            <span className="text-[10px] uppercase tracking-widest text-slate-600">💳 Einzahlungen</span>
            <div className="flex-1 h-px bg-[#2a3347]" />
          </div>

          <div className="space-y-2">
            {payments.map((p) => {
              const user = p.profiles;
              const name = user
                ? user.first_name || user.last_name
                  ? `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim()
                  : user.email ?? "Unbekannt"
                : "💰 Allgemein (für alle)";

              return (
                <div
                  key={p.id}
                  onClick={() => {
                    if (!isOrganizer) return;
                    setSelectedPayment(p);
                    setPaymentAmount(p.amount.toString());
                    setShowPaymentModal(true);
                  }}
                  className={`bg-[#161b27] rounded-xl border border-[#2a3347] px-4 py-3 flex justify-between items-center ${
                    isOrganizer ? "cursor-pointer hover:border-sky-500/20 transition-colors" : ""
                  }`}
                >
                  <div className="text-sm font-medium text-slate-100">{name}</div>
                  <div className="text-sm font-semibold text-emerald-400 tabular-nums shrink-0 ml-3">
                    +{p.amount.toFixed(2)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Modal: Ausgabe bearbeiten */}
      {showEditModal && selectedExpense && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-[#161b27] rounded-2xl border border-[#2a3347] p-6 w-full max-w-md relative">
            <button
              onClick={() => { setShowEditModal(false); setSelectedExpense(null); }}
              className="absolute top-3 right-4 text-slate-600 hover:text-slate-300 text-xl transition-colors"
              aria-label="Modal schließen"
            >
              &times;
            </button>
            <h2 className="text-sm font-semibold text-slate-100 mb-5">Ausgabe bearbeiten</h2>

            <input
              type="text"
              className="w-full bg-[#1e2639] border border-[#2a3347] rounded-xl text-slate-100 px-3 py-2 text-sm mb-3 focus:outline-none focus:border-sky-500/50"
              value={selectedExpense.title}
              onChange={(e) => setSelectedExpense({ ...selectedExpense, title: e.target.value })}
            />
            <input
              type="number"
              className="w-full bg-[#1e2639] border border-[#2a3347] rounded-xl text-slate-100 px-3 py-2 text-sm mb-3 focus:outline-none focus:border-sky-500/50"
              value={selectedExpense.amount}
              onChange={(e) => setSelectedExpense({ ...selectedExpense, amount: parseFloat(e.target.value) || 0 })}
            />
            <select
              className="w-full bg-[#1e2639] border border-[#2a3347] rounded-xl text-slate-100 px-3 py-2 text-sm mb-3 focus:outline-none focus:border-sky-500/50"
              value={selectedExpense.payer_id ?? "reisekasse"}
              onChange={(e) => setSelectedExpense({ ...selectedExpense, payer_id: e.target.value === "reisekasse" ? null : e.target.value })}
            >
              <option value="">– Zahler auswählen –</option>
              <option value="reisekasse">💼 Reisekasse</option>
              {participants.filter((p) => !p.is_blocked).map((p) => {
                const name = p.first_name || p.last_name
                  ? `${p.first_name ?? ""} ${p.last_name ?? ""}`.trim()
                  : p.email;
                return <option key={p.user_id} value={p.user_id}>{name}</option>;
              })}
            </select>
            <select
              className="w-full bg-[#1e2639] border border-[#2a3347] rounded-xl text-slate-100 px-3 py-2 text-sm mb-4 focus:outline-none focus:border-sky-500/50"
              value={selectedExpense.category}
              onChange={(e) => setSelectedExpense({ ...selectedExpense, category: e.target.value })}
            >
              <option value="Reisen">Reisen</option>
              <option value="Verpflegung">Verpflegung</option>
              <option value="Aktivitäten">Aktivitäten</option>
            </select>

            <div className="mb-5">
              <div className="text-[10px] uppercase tracking-widest text-slate-600 mb-2">Betroffene Teilnehmer</div>
              <div className="flex flex-wrap gap-2">
                {participants.filter((p) => !p.is_blocked).map((p) => {
                  const selected = selectedUsers.includes(p.user_id);
                  const label = p.first_name || p.last_name
                    ? `${p.first_name ?? ""} ${p.last_name ?? ""}`.trim()
                    : p.email;
                  return (
                    <button
                      key={p.user_id}
                      onClick={() => toggleUser(p.user_id)}
                      className={`px-2.5 py-1 rounded-full text-xs transition-colors ${
                        selected
                          ? "bg-sky-500/20 text-sky-400 border border-sky-500/30"
                          : "bg-[#1e2639] text-slate-400 hover:text-slate-200"
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex justify-between items-center">
              <button
                onClick={handleDelete}
                className="text-xs px-3 py-1.5 rounded-full bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors"
              >
                Löschen
              </button>
              <div className="flex gap-2">
                <button
                  onClick={() => { setShowEditModal(false); setSelectedExpense(null); }}
                  className="text-xs px-3 py-1.5 rounded-full bg-[#1e2639] text-slate-400 hover:text-slate-200 transition-colors"
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleEdit}
                  disabled={saving}
                  className="text-xs px-3 py-1.5 rounded-full bg-sky-600 text-white hover:bg-sky-500 transition-colors"
                >
                  {saving ? "Speichern…" : "Speichern"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Zahlung bearbeiten */}
      {showPaymentModal && selectedPayment && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-[#161b27] rounded-2xl border border-[#2a3347] p-6 w-full max-w-md relative">
            <button
              onClick={() => { setShowPaymentModal(false); setSelectedPayment(null); }}
              className="absolute top-3 right-4 text-slate-600 hover:text-slate-300 text-xl transition-colors"
              aria-label="Modal schließen"
            >
              &times;
            </button>
            <h2 className="text-sm font-semibold text-slate-100 mb-5">Zahlung bearbeiten</h2>
            <input
              type="number"
              className="w-full bg-[#1e2639] border border-[#2a3347] rounded-xl text-slate-100 px-3 py-2 text-sm mb-5 focus:outline-none focus:border-sky-500/50"
              value={paymentAmount}
              onChange={(e) => setPaymentAmount(e.target.value)}
            />
            <div className="flex justify-between items-center">
              <button
                onClick={handleDeletePayment}
                className="text-xs px-3 py-1.5 rounded-full bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors"
              >
                Löschen
              </button>
              <div className="flex gap-2">
                <button
                  onClick={() => { setShowPaymentModal(false); setSelectedPayment(null); }}
                  className="text-xs px-3 py-1.5 rounded-full bg-[#1e2639] text-slate-400 hover:text-slate-200 transition-colors"
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleUpdatePayment}
                  className="text-xs px-3 py-1.5 rounded-full bg-sky-600 text-white hover:bg-sky-500 transition-colors"
                >
                  Speichern
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Neue Zahlung */}
      {showNewPaymentModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-[#161b27] rounded-2xl border border-[#2a3347] p-6 w-full max-w-md relative">
            <button
              onClick={() => setShowNewPaymentModal(false)}
              className="absolute top-3 right-4 text-slate-600 hover:text-slate-300 text-xl transition-colors"
              aria-label="Modal schließen"
            >
              &times;
            </button>
            <h2 className="text-sm font-semibold text-slate-100 mb-5">Neue Zahlung erfassen</h2>
            <PaymentForm
              tripId={tripId}
              participants={participants.filter((p) => !p.is_blocked)}
              onSaved={() => {
                setShowNewPaymentModal(false);
                fetchExpenses();
                onChange?.();
              }}
              onCancel={() => setShowNewPaymentModal(false)}
              isOrganizer={isOrganizer}
            />
          </div>
        </div>
      )}

      {isOrganizer && !hideInternalFab && (
        <FloatingActionButton
          localStorageKey="seen_fab_accounting"
          actions={[
            {
              label: "💰 Ausgabe",
              onClick: () => onNewExpense ? onNewExpense() : setShowNewExpenseModal(true),
            },
            {
              label: "💳 Zahlung",
              onClick: () => setShowNewPaymentModal(true),
            },
          ]}
        />
      )}
      {showNewExpenseModal && (
        <NewExpenseForm
          tripId={tripId}
          participants={participants.filter((p) => !p.is_blocked)}
          onSaved={() => {
            setShowNewExpenseModal(false);
            fetchExpenses();
            onChange?.();
            onCloseModals?.(); // ← wichtig!
          }}
          onClose={() => {
            setShowNewExpenseModal(false);
            onCloseModals?.(); // ← wichtig!
          }}
        />
      )}
    </div>
  );
}
